import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import {
	type PostConnectOrganizationSchema,
	postConnectOrganizationSchema,
} from "@msuite/utils/schemas";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { ConnectPartnerForm } from "@/forms/connect-partner-form";
import { client } from "@/hono";
import { ConnectPartnerConfirmation } from "@/components/partners/connect-partner-confirmation";

/** Props Interface */
interface ConnectPartnerModalProps extends ModalProps {}

/**
 * Connects the viewer's organisation as the partner of the organisation that owns a code. The code
 * is previewed first, so the viewer sees which organisation will book its crews before the row is
 * written; a code whose owner already hosts the viewer offers the partner's page instead.
 */
export function ConnectPartnerModal({ isOpen, onClose }: ConnectPartnerModalProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const qc = useQueryClient();
	const navigate = useNavigate();
	const methods = useForm<PostConnectOrganizationSchema>({
		defaultValues: { code: "" },
		resolver: resolver(postConnectOrganizationSchema),
	});

	/** State */
	const [preview, setPreview] = useState<Awaited<ReturnType<typeof fetchPreview>> | null>(null);

	/** Functions */
	async function fetchPreview(code: string) {
		const response = await client.organizations.connect[":code"].$get({ param: { code } });
		const data = await responseHandler(response);
		return { organization: data.organization, partnership: data.partnership };
	}

	async function handleOnPreview(json: PostConnectOrganizationSchema) {
		setPreview(await fetchPreview(json.code));
	}

	async function handleOnConnect(json: PostConnectOrganizationSchema) {
		if (!preview) return;
		if (preview.partnership) {
			handleOnOpenPartner(preview.organization.id);
			return;
		}
		const response = await client.organizations.connect.$post({ json });
		const data = await responseHandler(response);
		await qc.invalidateQueries({ predicate: invalidate(["organizationPartnerships"]) });
		toast.success(t("organization.connected_organization_successfully"));
		handleOnOpenPartner(data.partnership.hostOrgId);
	}

	function handleOnOpenPartner(hostOrgId: string) {
		navigate(`/partners/${hostOrgId}`);
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				title={
					preview
						? t("organization.connect_confirmation_title")
						: t("organization.connect_organization")
				}
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(preview ? handleOnConnect : handleOnPreview)}
			>
				<Modal.Body>
					{preview ? (
						<ConnectPartnerConfirmation
							organization={preview.organization}
							partnership={preview.partnership}
						/>
					) : (
						<ConnectPartnerForm />
					)}
				</Modal.Body>
				{preview ? (
					<Modal.FooterForm
						submitLabel={
							preview.partnership
								? t("organization.open_partner")
								: t("organization.connect_organization")
						}
					/>
				) : (
					<Modal.FooterForm />
				)}
			</Modal>
		</FormProvider>
	);
}
